import { getIdToken, apiClient } from "../api";
/**
 * Create a new Interactive Story
 */
export const createInteractiveStory = async ({ title, description, cover, categories }) => {
  try {
    const idToken = await getIdToken();

    const formData = { title, description, categories };

    // Convert cover image to base64
    if (cover) {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result;
          if (result.includes(",")) {
            resolve(result.split(",")[1]); // Extract only base64 content
          } else {
            reject(new Error("Invalid base64 format."));
          }
        };
        reader.onerror = () => reject(new Error("Failed to read cover image file."));
        reader.readAsDataURL(cover);
      });

      const fileNameParts = cover.name.split(".");
      const coverImageExtension = fileNameParts.length > 1 ? fileNameParts.pop().toLowerCase() : "";

      if (!coverImageExtension) {
        throw new Error("Invalid cover image format.");
      }

      formData.coverImageBuffer = base64;
      formData.coverImageExtension = coverImageExtension;
    }

    // Call backend endpoint for interactive stories
    const response = await apiClient.post("/interactiveEditorFunctions-createInteractiveStory", formData, {
      headers: { Authorization: `Bearer ${idToken}` },
    });

    return response.data.storyID;
  } catch (error) {
    console.error("Error creating interactive story:", error.response?.data || error.message);

    if (error.response?.status === 400) {
      throw new Error(error.response.data.message || "Moderation failed. Please check your content.");
    }

    throw new Error(error.response?.data?.message || error.message);
  }
};

export const getInteractiveStoryDetails = async (storyID) => {
  if (!storyID) throw new Error("Missing storyID.");

  try {
    const idToken = await getIdToken();

    const response = await apiClient.post(
      "/interactiveEditorFunctions-getInteractiveStoryDetails",
      { storyID },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );

    if (!response?.data?.success) {
      throw new Error(response?.data?.message || "Failed to fetch interactive story details.");
    }

    return response.data.story; // { ...story, chapters: [], characters: [], backgrounds: [] }
  } catch (error) {
    console.error("Error fetching interactive story details:", error.response?.data || error.message);

    // Pass through useful server messages (403, 404, etc.)
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }

    throw new Error(error.message || "Failed to fetch interactive story details.");
  }
};

export const fetchUserInteractiveStories = async () => {
  const idToken = await getIdToken();
  const res = await apiClient.post(
    "/interactiveEditorFunctions-listInteractiveStories",
    {},
    { headers: { Authorization: `Bearer ${idToken}` } }
  );

  const list = res?.data?.stories || [];
  return list.map(s => ({
    ...s,
    id: s.id || s.storyID || s.storyId, // normalize id
    type: "interactive",
  }));
};

export const getCharacters = async (storyID) => {
  if (!storyID) throw new Error("Missing storyID.");

  try {
    const idToken = await getIdToken();

    const response = await apiClient.post(
      "/interactiveEditorFunctions-getCharacters",
      { storyID },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );

    if (!response?.data?.success) {
      throw new Error(response?.data?.message || "Failed to fetch characters.");
    }

    // Normalize and return array of characters
    return response.data.characters || [];
  } catch (error) {
    console.error("Error fetching characters:", error.response?.data || error.message);
    throw new Error(error.response?.data?.message || "Failed to fetch characters.");
  }
};

/**
 * Add or Edit a character (same endpoint, backend decides based on characterID)
 */
export const addCharacter = async (storyID, data) => {
  if (!storyID) throw new Error("Missing storyID.");
  if (!data?.name) throw new Error("Character name is required.");

  try {
    const idToken = await getIdToken();

    const response = await apiClient.post(
      "/interactiveEditorFunctions-addOrEditCharacter",
      { storyID, ...data },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );

    if (!response?.data?.success) {
      throw new Error(response?.data?.message || "Failed to save character.");
    }

    return response.data.character || null;
  } catch (error) {
    console.error("Error adding/editing character:", error.response?.data || error.message);
    throw new Error(error.response?.data?.message || "Failed to save character.");
  }
};

/**
 * Edit a character (optional separate alias)
 * We simply call the same endpoint, but include characterID.
 */
export const editCharacter = async (storyID, characterID, data) => {
  if (!storyID || !characterID) throw new Error("Missing storyID or characterID.");

  try {
    const idToken = await getIdToken();

    const response = await apiClient.post(
      "/interactiveEditorFunctions-addOrEditCharacter",
      { storyID, characterID, ...data },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );

    if (!response?.data?.success) {
      throw new Error(response?.data?.message || "Failed to update character.");
    }

    return response.data.character || null;
  } catch (error) {
    console.error("Error updating character:", error.response?.data || error.message);
    throw new Error(error.response?.data?.message || "Failed to update character.");
  }
};

/**
 * Delete a character
 */
export const deleteCharacter = async (storyID, characterID) => {
  if (!storyID || !characterID) throw new Error("Missing storyID or characterID.");

  try {
    const idToken = await getIdToken();

    const response = await apiClient.post(
      "/interactiveEditorFunctions-deleteCharacter",
      { storyID, characterID },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );

    if (!response?.data?.success) {
      throw new Error(response?.data?.message || "Failed to delete character.");
    }

    return true;
  } catch (error) {
    console.error("Error deleting character:", error.response?.data || error.message);
    throw new Error(error.response?.data?.message || "Failed to delete character.");
  }
};

export const getInteractiveStoryChapter = async (storyID, chapterID) => {
  if (!storyID) throw new Error("Missing storyID.");
  if (!chapterID) throw new Error("Missing chapterID.");

  try {
    const idToken = await getIdToken();

    const res = await apiClient.post(
      "/interactiveEditorFunctions-getInteractiveStoryChapter",
      { storyID, chapterID },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );

    if (!res?.data?.success) {
      throw new Error(res?.data?.message || "Failed to fetch chapter.");
    }

    const chapter = res.data.chapter || {};
    const scenes = Array.isArray(chapter.scenes) ? chapter.scenes : [];
    const edges = Array.isArray(chapter.edges) ? chapter.edges : [];

    // Ensure stable ordering for UI lists
    scenes.sort((a, b) => {
      const ai = a.index ?? Number.MAX_SAFE_INTEGER;
      const bi = b.index ?? Number.MAX_SAFE_INTEGER;
      if (ai !== bi) return ai - bi;
      return String(a.id).localeCompare(String(b.id));
    });

    return {
      ...chapter,
      scenes,
      edges,
    };
  } catch (error) {
    console.error("Error fetching interactive chapter:", error.response?.data || error.message);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error(error.message || "Failed to fetch chapter.");
  }
};

export const createInteractiveChapter = async (storyID, chapterIndex, opts = {}) => {
  if (!storyID) throw new Error("Missing storyID.");
  if (chapterIndex == null) throw new Error("Missing chapterIndex.");

  try {
    const idToken = await getIdToken();
    const payload = {
      storyID,
      chapterIndex: Number(chapterIndex),
      ...(opts.title ? { title: opts.title } : {}),
    };

    const res = await apiClient.post(
      "/interactiveEditorFunctions-createInteractiveChapter",
      payload,
      { headers: { Authorization: `Bearer ${idToken}` } }
    );

    if (!res?.data?.success || !res?.data?.chapterID) {
      throw new Error(res?.data?.message || "Failed to create chapter.");
    }

    return res.data.chapterID;
  } catch (error) {
    console.error("Error creating interactive chapter:", error.response?.data || error.message);
    throw new Error(error.response?.data?.message || error.message || "Failed to create chapter.");
  }
};

/**
 * Save a scene (content + branching) and create/rewire children server-side.
 * `data` may include: { background, lines, mode, continue, choices }
 *  - continue: { type: 'new' | 'existing', childId? }
 *  - choices:  [{ text, type: 'new'|'existing', childId? }, ...]
 */
export const saveInteractiveScene = async ({ storyID, chapterID, sceneID, data }) => {
  if (!storyID) throw new Error("Missing storyID.");
  if (!chapterID) throw new Error("Missing chapterID.");
  if (!sceneID) throw new Error("Missing sceneID.");

  try {
    const idToken = await getIdToken();
    const res = await apiClient.post(
      "/interactiveEditorFunctions-saveInteractiveScene",
      { storyID, chapterID, sceneID, data },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
    if (!res?.data?.success) {
      throw new Error(res?.data?.message || "Failed to save scene.");
    }
    const rawEdges = Array.isArray(res.data.edges) ? res.data.edges : [];
    const edges = rawEdges.map((e, i) => ({
      id: e.id || `${String(e.source)}->${String(e.target)}-${i}`,
      source: String(e.source),
      target: String(e.target),
    }));
    return {
      createdChildren: res.data.createdChildren || [],
      updatedSceneId: res.data.updatedSceneId || sceneID,
      edges,
    };
  } catch (error) {
    console.error("Error saving scene:", error.response?.data || error.message);
    throw new Error(error.response?.data?.message || error.message || "Failed to save scene.");
  }
};

/**
 * Publish an interactive chapter.
 * Server will:
 *  - mirror draft → Stories (Scenes, edges, metadata)
 *  - overwrite Characters in published story with draft Characters
 *  - set chapter status (e.g., 'PUBLISHED') and timestamps
 *  - (optionally) copy assets like backgrounds if backend supports it
 *
 * @param {string} storyID
 * @param {string} chapterID
 * @param {object} options
 *  - overwriteCharacters {boolean} default true
 *  - copyBackgrounds {boolean} default true (noop if backend ignores)
 *  - visibility { 'public' | 'unlisted' | 'private' } optional
 */
export const publishInteractiveChapter = async (storyID, chapterID, options = {}) => {
  if (!storyID) throw new Error("Missing storyID.");
  if (!chapterID) throw new Error("Missing chapterID.");

  const {
    overwriteCharacters = true,
    copyBackgrounds = true,
    visibility, // optional; backend may ignore
  } = options;

  try {
    const idToken = await getIdToken();
    const payload = {
      storyID,
      chapterID,
      overwriteCharacters,
      copyBackgrounds,
      ...(visibility ? { visibility } : {}),
    };

    const res = await apiClient.post(
      "/interactiveEditorFunctions-publishInteractiveChapter",
      payload,
      { headers: { Authorization: `Bearer ${idToken}` } }
    );

    if (!res?.data?.success) {
      throw new Error(res?.data?.message || "Failed to publish chapter.");
    }

    // Normalize a helpful return
    return {
      chapterID: res.data.chapterID || chapterID,
      status: res.data.status || "PUBLISHED",
      publishedAt: res.data.publishedAt || null,
      storyID,
    };
  } catch (error) {
    console.error("Error publishing interactive chapter:", error.response?.data || error.message);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error(error.message || "Failed to publish chapter.");
  }
};

export const updateInteractiveChapterTitle = async (storyID, chapterID, title) => {
  if (!storyID) throw new Error("Missing storyID.");
  if (!chapterID) throw new Error("Missing chapterID.");
  const next = (title ?? "").trim();
  if (!next) throw new Error("Title cannot be empty.");
  if (next.length > 60) throw new Error("Title must be 60 characters or fewer.");

  try {
    const idToken = await getIdToken();
    const res = await apiClient.post(
      "/interactiveEditorFunctions-updateInteractiveChapterTitle",
      { storyID, chapterID, title: next },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );

    if (!res?.data?.success) {
      throw new Error(res?.data?.message || "Failed to update chapter title.");
    }
    return res.data; // { success: true, title, updatedAt }
  } catch (error) {
    const msg =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to update chapter title.";
    throw new Error(msg);
  }
};

/**
 * Edit interactive story metadata (+ optional cover replace).
 * Accepts a File for `cover` and converts it to base64 like createInteractiveStory.
 */
export const editInteractiveStory = async ({ storyID, title, description, categories, cover }) => {
  if (!storyID) throw new Error("Missing storyID.");
  if (!title || !description || !categories) throw new Error("Missing required fields.");

  try {
    const idToken = await getIdToken();

    const payload = { storyID, title, description, categories };

    if (cover) {
      // to base64 (same pattern as createInteractiveStory)
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result;
          if (typeof result === "string" && result.includes(",")) {
            resolve(result.split(",")[1]);
          } else {
            reject(new Error("Invalid base64 format."));
          }
        };
        reader.onerror = () => reject(new Error("Failed to read cover image file."));
        reader.readAsDataURL(cover);
      });

      const parts = cover.name.split(".");
      const ext = parts.length > 1 ? parts.pop().toLowerCase() : "";
      if (!ext) throw new Error("Invalid cover image format.");

      payload.coverImageBuffer = base64;
      payload.coverImageExtension = ext;
    }

    const res = await apiClient.post(
      "/interactiveEditorFunctions-editInteractiveStory",
      payload,
      { headers: { Authorization: `Bearer ${idToken}` } }
    );

    if (!res?.data?.success) throw new Error(res?.data?.message || "Failed to edit story.");
    return res.data; // refreshed draft
  } catch (error) {
    console.error("Error editing interactive story:", error.response?.data || error.message);
    throw new Error(error.response?.data?.message || error.message || "Failed to edit interactive story.");
  }
};

/**
 * Toggle Active flag. If `isActive` is undefined, backend will invert current.
 */
export const toggleActiveInteractiveStory = async (storyID, isActive) => {
  if (!storyID) throw new Error("Missing storyID.");
  try {
    const idToken = await getIdToken();
    const res = await apiClient.post(
      "/interactiveEditorFunctions-toggleActiveInteractiveStory",
      { storyID, ...(typeof isActive === "boolean" ? { isActive } : {}) },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
    if (!res?.data?.success) throw new Error(res?.data?.message || "Failed to toggle active.");
    return !!res.data.isActive;
  } catch (error) {
    console.error("Error toggling active:", error.response?.data || error.message);
    throw new Error(error.response?.data?.message || error.message || "Failed to toggle active.");
  }
};

/**
 * Toggle Published flag for the whole story (requires at least one published chapter + real cover to turn ON).
 */
export const togglePublishInteractiveStory = async (storyID) => {
  if (!storyID) throw new Error("Missing storyID.");
  try {
    const idToken = await getIdToken();
    const res = await apiClient.post(
      "/interactiveEditorFunctions-togglePublishInteractiveStory",
      { storyID },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
    if (!res?.data?.success) throw new Error(res?.data?.message || "Failed to toggle publish.");
    return !!res.data.isPublished;
  } catch (error) {
    console.error("Error toggling publish:", error.response?.data || error.message);
    throw new Error(error.response?.data?.message || error.message || "Failed to toggle publish.");
  }
};

/**
 * Delete an interactive chapter (ONLY the last chapter allowed by backend).
 * Expects the DRAFT chapter doc id (NOT the index).
 * Returns useful metadata (index removed, scenesRemoved, etc.)
 */
export const deleteInteractiveChapter = async (storyID, chapterID) => {
  if (!storyID) throw new Error("Missing storyID.");
  if (!chapterID) throw new Error("Missing chapterID.");
  try {
    const idToken = await getIdToken();
    const res = await apiClient.post(
      "/interactiveEditorFunctions-deleteInteractiveChapter",
      { storyID, chapterID },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
    if (!res?.data?.success) throw new Error(res?.data?.message || "Failed to delete chapter.");
    return {
      index: res.data.index,
      chapterID: res.data.chapterID,
      scenesRemoved: res.data.scenesRemoved ?? 0,
      publishedStoryUnpublished: !!res.data.publishedStoryUnpublished,
    };
  } catch (error) {
    console.error("Error deleting chapter:", error.response?.data || error.message);
    throw new Error(error.response?.data?.message || error.message || "Failed to delete chapter.");
  }
};

/**
 * Permanently delete an entire interactive story (draft + published mirrors + storage).
 */
export const deleteInteractiveStory = async (storyID) => {
  if (!storyID) throw new Error("Missing storyID.");
  try {
    const idToken = await getIdToken();
    const res = await apiClient.post(
      "/interactiveEditorFunctions-deleteInteractiveStory",
      { storyID },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
    if (!res?.data?.success) throw new Error(res?.data?.message || "Failed to delete story.");
    return true;
  } catch (error) {
    console.error("Error deleting interactive story:", error.response?.data || error.message);
    throw new Error(error.response?.data?.message || error.message || "Failed to delete story.");
  }
};

// NEW: Create a free (unconnected) scene in a chapter
export const createFreeInteractiveScene = async ({ storyID, chapterID, data = {} }) => {
  if (!storyID) throw new Error("Missing storyID.");
  if (!chapterID) throw new Error("Missing chapterID.");

  try {
    const idToken = await getIdToken();
    const res = await apiClient.post(
      "/interactiveEditorFunctions-createFreeInteractiveScene",
      { storyID, chapterID, data },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );

    if (!res?.data?.success) {
      throw new Error(res?.data?.message || "Failed to create free scene.");
    }

    const sceneId = res.data.sceneId;
    const scene = res.data.scene || null;

    const rawEdges = Array.isArray(res.data.edges) ? res.data.edges : [];
    const edges = rawEdges.map((e, i) => ({
      id: e.id || `${String(e.source)}->${String(e.target)}-${i}`,
      source: String(e.source),
      target: String(e.target),
    }));

    return { sceneId, scene, edges };
  } catch (error) {
    console.error("Error creating free scene:", error.response?.data || error.message);
    throw new Error(error.response?.data?.message || error.message || "Failed to create free scene.");
  }
};

/**
 * Delete a single interactive scene from a chapter and return updated edges.
 * Backend also scrubs all references (parents/children/choices/continue).
 */
export const deleteInteractiveScene = async ({ storyID, chapterID, sceneID }) => {
  if (!storyID) throw new Error("Missing storyID.");
  if (!chapterID) throw new Error("Missing chapterID.");
  if (!sceneID) throw new Error("Missing sceneID.");

  try {
    const idToken = await getIdToken();
    const res = await apiClient.post(
      "/interactiveEditorFunctions-deleteInteractiveScene",
      { storyID, chapterID, sceneID },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );

    if (!res?.data?.success) {
      throw new Error(res?.data?.message || "Failed to delete scene.");
    }

    const rawEdges = Array.isArray(res.data.edges) ? res.data.edges : [];
    const edges = rawEdges.map((e, i) => ({
      id: e.id || `${String(e.source)}->${String(e.target)}-${i}`,
      source: String(e.source),
      target: String(e.target),
    }));

    return {
      sceneID: res.data.sceneID || sceneID,
      scenesTouched: res.data.scenesTouched ?? 0,
      edges,
    };
  } catch (error) {
    console.error("Error deleting interactive scene:", error.response?.data || error.message);
    throw new Error(
      error.response?.data?.message ||
      error.message ||
      "Failed to delete scene."
    );
  }
};
/**
 * Validate an interactive chapter's graph + content.
 * Returns:
 *  - readyToPublish: boolean
 *  - errors: { graph[], deadEnds[], brokenChainScenes[], missingChoiceText[] }
 *  - warnings: { stranglers[], missingLines[] }
 *  - meta: { sceneCount, startSceneId, endSceneId }
 */
export const validateInteractiveChapter = async (storyID, chapterID) => {
  if (!storyID) throw new Error("Missing storyID.");
  if (!chapterID) throw new Error("Missing chapterID.");

  try {
    const idToken = await getIdToken();
    const res = await apiClient.post(
      "/interactiveEditorFunctions-validateInteractiveChapter",
      { storyID, chapterID },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );

    const data = res?.data;

    // Backend responded, but with success: false (e.g. 4xx/5xx from function)
    if (!data?.success) {
      const err = new Error(data?.message || "Failed to validate chapter.");
      // preserve backend metadata for localization / UI decisions
      err.code = data?.code || "VALIDATION_FAILED";
      err.status = res.status;
      err.payload = data;
      throw err;
    }

    const {
      readyToPublish,
      errors = {},
      warnings = {},
      meta = {},
      storyID: returnedStoryID,
      chapterID: returnedChapterID,
    } = data;

    // You’ll use these error types in the UI for localization:
    // errors.graph[].type, errors.deadEnds[], errors.brokenChainScenes[], etc.
    return {
      storyID: returnedStoryID || storyID,
      chapterID: returnedChapterID || chapterID,
      readyToPublish: !!readyToPublish,
      errors: {
        graph: errors.graph || [],
        deadEnds: errors.deadEnds || [],
        brokenChainScenes: errors.brokenChainScenes || [],
        missingChoiceText: errors.missingChoiceText || [],
      },
      warnings: {
        stranglers: warnings.stranglers || [],
        missingLines: warnings.missingLines || [],
      },
      meta,
    };
  } catch (error) {
    console.error(
      "Error validating interactive chapter:",
      error.response?.data || error
    );

    // Axios error with a response from backend → wrap but keep code/status
    if (error.response?.data) {
      const { code, message } = error.response.data;
      const err = new Error(
        message || "Failed to validate chapter."
      );
      err.code = code || "UNKNOWN_VALIDATION_ERROR";
      err.status = error.response.status;
      err.payload = error.response.data;
      throw err;
    }

    // Network / unexpected error
    throw error;
  }
};

/**
 * List all published interactive stories available to the reader.
 * Uses: interactiveEditorFunctions-listPublishedInteractiveStories
 */
export const fetchPublishedInteractiveStories = async () => {
  try {
    const idToken = await getIdToken();

    const res = await apiClient.post(
      "/interactiveEditorFunctions-listPublishedInteractiveStories",
      {},
      { headers: { Authorization: `Bearer ${idToken}` } }
    );

    if (!res?.data?.success) {
      throw new Error(res?.data?.message || "Failed to fetch published interactive stories.");
    }

    const list = res.data.stories || [];

    return list.map((s) => ({
      ...s,
      id: s.id || s.storyID || s.storyId,
      type: "interactive",
    }));
  } catch (error) {
    console.error("Error fetching published interactive stories:", error.response?.data || error.message);
    throw new Error(error.response?.data?.message || error.message || "Failed to fetch published interactive stories.");
  }
};

/**
 * Get a single published interactive story (metadata + chapters, characters…).
 * Uses: interactiveEditorFunctions-getPublishedInteractiveStory
 */
export const getPublishedInteractiveStory = async (storyID) => {
  if (!storyID) throw new Error("Missing storyID.");

  try {
    const idToken = await getIdToken();

    const res = await apiClient.post(
      "/interactiveEditorFunctions-getPublishedInteractiveStory",
      { storyID },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );

    if (!res?.data?.success) {
      throw new Error(res?.data?.message || "Failed to fetch published interactive story.");
    }

    return res.data.story;
  } catch (error) {
    console.error("Error fetching published interactive story:", error.response?.data || error.message);
    throw new Error(error.response?.data?.message || error.message || "Failed to fetch published interactive story.");
  }
};

/**
 * Get a single published interactive chapter (scenes + edges) for reading.
 * You can call either:
 *   getPublishedInteractiveChapter(storyID, 1)             // by chapterIndex
 *   getPublishedInteractiveChapter(storyID, { chapterID }) // by chapterID
 *
 * Uses: interactiveEditorFunctions-getPublishedInteractiveChapter
 */
export const getPublishedInteractiveChapter = async (storyID, chapterArg) => {
  if (!storyID) throw new Error("Missing storyID.");

  let chapterIndex = undefined;
  let chapterID = undefined;

  if (typeof chapterArg === "number" || typeof chapterArg === "string") {
    // Treat as index
    chapterIndex = chapterArg;
  } else if (chapterArg && typeof chapterArg === "object") {
    chapterIndex = chapterArg.chapterIndex;
    chapterID = chapterArg.chapterID;
  }

  if (chapterIndex == null && !chapterID) {
    throw new Error("Missing chapterIndex or chapterID.");
  }

  try {
    const idToken = await getIdToken();

    const payload = {
      storyID,
      ...(chapterID ? { chapterID } : { chapterIndex }),
    };

    const res = await apiClient.post(
      "/interactiveEditorFunctions-getPublishedInteractiveChapter",
      payload,
      { headers: { Authorization: `Bearer ${idToken}` } }
    );

    if (!res?.data?.success) {
      throw new Error(res?.data?.message || "Failed to fetch published interactive chapter.");
    }

    const chapter = res.data.chapter || {};
    const scenes = Array.isArray(chapter.scenes) ? chapter.scenes : [];
    const edges = Array.isArray(chapter.edges) ? chapter.edges : [];

    // Keep sorting logic consistent with draft chapter fetch
    scenes.sort((a, b) => {
      const ai = a.index ?? Number.MAX_SAFE_INTEGER;
      const bi = b.index ?? Number.MAX_SAFE_INTEGER;
      if (ai !== bi) return ai - bi;
      return String(a.id).localeCompare(String(b.id));
    });

    return {
      ...chapter,
      scenes,
      edges,
    };
  } catch (error) {
    console.error("Error fetching published interactive chapter:", error.response?.data || error.message);
    throw new Error(error.response?.data?.message || error.message || "Failed to fetch published interactive chapter.");
  }
};

export const exportInteractiveChapterScorm = async (storyID, chapterIndex) => {
  if (!storyID) throw new Error("Missing storyID.");
  if (chapterIndex == null) throw new Error("Missing chapterIndex.");

  try {
    const idToken = await getIdToken();

    const res = await apiClient.post(
      "/interactiveScorm-generateChapterScormPackage",
      {
        storyID,
        chapterID: chapterIndex,
      },
      {
        headers: { Authorization: `Bearer ${idToken}` },
      }
    );

    if (!res?.data?.success) {
      throw new Error(res?.data?.message || "Failed to export SCORM package.");
    }

    const url =
      res.data.downloadUrl ||
      res.data.zipUrl ||
      res.data.url;

    if (!url) {
      throw new Error("SCORM export succeeded but no download URL was returned.");
    }

    return url;
  } catch (error) {
    console.error(
      "Error exporting SCORM for interactive chapter:",
      error.response?.data || error.message
    );
    throw new Error(
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      "Failed to export SCORM package."
    );
  }
};

